import React, { CSSProperties, Fragment } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { CircleAlert, Lock, MoveLeft, MoveRight } from 'lucide-react';
import { DynamicIcon, IconName } from 'lucide-react/dynamic';

import { useGetScenarioWithPersonas } from '../../../hooks/mbts-ux';

import { IMBTSPersonaWithStatus } from '../../../types/services.interfaces/mbts-ux';

import { getScenarioColor } from '../../../helpers/misc.helper';

export const MBTSPersonas = () => {
    const { scenarioId } = useParams();

    const navigate = useNavigate();

    const { data: scenario, isLoading, error: scenarioError } = useGetScenarioWithPersonas(scenarioId ?? null);

    const error = (scenarioError as any)?.response?.data;
    const entitlementError =
        error?.statusCode === 403
            ? error.message || 'You are not entitled to access this scenario.'
            : null;

    const handleConversation = (personaId: string) => {
        if (!scenarioId || !scenario) return;

        navigate(`/mbts/${scenarioId}/persona/${personaId}/details`);
    };

    const onViewAnalysis = () => {
        if (!scenarioId || !scenario) return;

        if (scenario.personas.some(persona => persona.status !== 'completed')) return;

        navigate(`/mbts/${scenarioId}/analysis`);
    };

    const isPersonaLocked = (idx: number, persona: IMBTSPersonaWithStatus): boolean => {
        if (!scenario) return true;

        return idx > 0 && scenario.personas[idx - 1].status !== 'completed' && persona.status === 'not-started';
    };

    return (
        <div
            className='mbts_personas__page'
            style={{
                '--scenario-theme-color-main': getScenarioColor(scenario ?? null).main,
                '--scenario-theme-color-bg': getScenarioColor(scenario ?? null).bg
            } as CSSProperties}
        >
            {isLoading && !scenario && !entitlementError && !error && (
                <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <div className='list-loader' />
                    <p className='text-medium color--txt-secondary'>Loading personas</p>
                </div>
            )}

            {!isLoading && !!entitlementError && (
                <div className='mbts_personas__top_sec'>
                    <div className='mbts_personas__top_sec__icon mbts_personas__top_sec__icon--locked'>
                        <Lock />
                    </div>
                    <p className='text-3xl text-bold mb-xs text-center'>Access Restricted</p>
                    <p className='color--txt-tertiary text-center'>{entitlementError}</p>
                </div>
            )}
            {!isLoading && !entitlementError && !!error && (
                <div className='mt-2xl d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <CircleAlert className='color--error w-3xl h-3xl' />
                    <p className='text-2xl text-semibold text-center'>Error occurred</p>
                    <p className='color--txt-tertiary text-center'>{error?.message || 'An error occurred while loading the personas.'}</p>
                </div>
            )}

            {!isLoading && !scenario && !entitlementError && !error && (
                <p className='text-lg text-semibold color--txt-tertiary text-center'>No persona found!</p>
            )}
            {!isLoading && !!scenario && !entitlementError && !error && (
                <Fragment>
                    <div className='mbts_personas__top_sec'>
                        <div className='mbts_personas__top_sec__icon'>
                            <DynamicIcon name={scenario.icon as IconName} />
                        </div>
                        <p className='text-3xl text-bold mb-xs text-center'>{scenario.title}</p>
                        <p className='color--txt-tertiary text-center'>
                            {scenario.personas.length === 1
                                ? 'Are you up for the challenge?'
                                : 'Choose a conversation to practice your stakeholder management skills'
                            }
                        </p>
                    </div>

                    <div className='card card--md card--radius-md mbts_personas__scenario mb-2xl'>
                        <p className='text-lg text-semibold mb-xl color--txt-secondary'><span className='color--txt-muted'>Scenario:</span> {scenario.title}</p>
                        <div className='mbts_personas__scenario__situation'>
                            <p className='text-semibold mb-xs color--txt-tertiary'>Situation:</p>
                            <div className='mbts_personas__scenario__situation__text' dangerouslySetInnerHTML={{ __html: scenario.situation }} />
                        </div>
                    </div>

                    <p className='text-sm text-semibold color--txt-disabled tracking-wider mb-lg'>CHOOSE A PERSONA</p>
                    <div className='mbts_personas__list'>
                        {scenario.personas.map((persona, idx) => (
                            <div
                                key={persona.id}
                                className={`card card--md card--radius-md mbts_personas__list__persona ${isPersonaLocked(idx, persona) ? 'mbts_personas__list__persona--locked' : 'card--hover-shadow'}`}
                            >
                                <div className='mbts_personas__list__persona__icon'>
                                    <DynamicIcon name={persona.icon as IconName} />
                                </div>
                                <div className='w-full'>
                                    <div className='d-flex justify-content-between gap-lg'>
                                        <div>
                                            <div className='d-flex align-items-center gap-sm'>
                                                <p className='text-xl text-bold'>{persona.person}</p>
                                                {persona.status === 'completed' &&
                                                    <p className='br-full px-sm py-xs text-xs text-medium color--success bg--success-bg'>Completed</p>
                                                }
                                            </div>
                                            <p className='color--txt-tertiary mb-sm'>{persona.title}</p>
                                        </div>
                                        <div>
                                            {
                                                isPersonaLocked(idx, persona)
                                                    ? <div className='d-flex align-items-center gap-xs br-full px-md py-xs bg--n300'>
                                                        <Lock className='w-md h-md color--txt-disabled' />
                                                        <p className='text-sm color--txt-disabled text-medium'>Locked</p>
                                                    </div>
                                                    : <Fragment>
                                                        {persona.status === 'not-started' &&
                                                            <button
                                                                onClick={() => handleConversation(persona.id)}
                                                                className='btn btn--sm btn--radius-sm btn--primary'
                                                            >
                                                                Start
                                                            </button>
                                                        }
                                                        {persona.status === 'in-progress' &&
                                                            <button
                                                                onClick={() => handleConversation(persona.id)}
                                                                className='btn btn--sm btn--radius-sm btn--primary'
                                                            >
                                                                Continue
                                                            </button>
                                                        }
                                                        {persona.status === 'completed' &&
                                                            <button
                                                                onClick={() => handleConversation(persona.id)}
                                                                className='btn btn--sm btn--radius-sm btn--secondary'
                                                            >
                                                                Retry
                                                            </button>
                                                        }
                                                    </Fragment>
                                            }
                                        </div>
                                    </div>
                                    <p className='text-sm color--txt-muted'>{persona.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Fragment>
            )}

            <div className='mbts_personas__actions'>
                <Link to='/mbts/scenarios' className='link link--primary link--flex'>
                    <MoveLeft />
                    <span>Back to Scenarios</span>
                </Link>
                {!entitlementError && !error && (
                    <button
                        className='btn btn--md btn--radius-md btn--primary'
                        disabled={scenario?.personas.some(p => p.status !== 'completed')}
                        onClick={onViewAnalysis}
                    >
                        View Complete Analysis
                        <MoveRight />
                    </button>
                )}
            </div>
        </div>
    );
};